import { Fragment } from 'react';

import { library } from '@fortawesome/fontawesome-svg-core';
import {
  ListGroup,
  ListGroupItem,
  OverlayTrigger,
  Popover,
} from 'react-bootstrap';
import {
  faBullhorn,
  faChevronRight,
  faBell,
} from '@fortawesome/free-solid-svg-icons';

import { axiosInstance } from 'src/api/axios';
import APIEndpoints from 'src/Member/api/APIEndpoints';
import downloadTypes from 'src/common/downloadTypes.json';
import { arrayBufferToBase64 } from 'src/Member/utils/common';
import MasterPermissions from 'src/Member/api/MasterPermissions';
import useDate from '../../utils/useDate';
import { ResourcesSVG } from '../../img/GlobalSVG';
import './Resources.scss';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

library.add(faBullhorn, faChevronRight, faBell);

const downloadFile = async (parameter, fileName) => {
  let extension = '.pdf';

  const documentData = await axiosInstance.get(
    APIEndpoints.getDocument() + parameter,
    { responseType: 'arraybuffer' },
  );

  const contentType = documentData.headers['content-type'];
  const extensionarr = downloadTypes.filter(
    (d) => d.contentType === contentType?.toLowerCase(),
  );

  fileName = fileName ? fileName : 'download';

  if (extensionarr && extensionarr?.length)
    extension = extensionarr[0].extension;

  if ('.pdf' === extension) {
    const binaryData = [documentData.data];
    const fileURL = window.URL.createObjectURL(
      new Blob(binaryData, { type: 'application/pdf' }),
    );
    const link = document.createElement('a');
    link.setAttribute('href', fileURL);
    link.setAttribute('download', fileName + extension);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    //window.open(fileURL);
  } else {
    const link = document.createElement('a');
    const actualData = arrayBufferToBase64(documentData.data);

    link.setAttribute('href', 'data:' + contentType + ';base64,' + actualData);
    link.setAttribute('download', fileName + extension);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  }
};

const getResourceCards = (resourceItem, truncateResourceTitle, formatDate) => {
  if ('External Link' === resourceItem.ContentType) {
    if (
      resourceItem.ResourceTitle === 'Find Provider Links' &&
      !MasterPermissions.CheckPermission('Access Network Links')
    ) {
      return null;
    }

    return (
      <>
        <div className="resource-file-type">
          <div className="resource-item-info-icon">
            <ResourcesSVG.ExternalLinkSVG className="fill-color" />
          </div>
        </div>

        {/*FUTURE: we can convert this block to resuable component */}
        {
          <div className="resource-content">
            <a
              className={
                truncateResourceTitle
                  ? 'resource-link-title'
                  : 'display-full-resource-link-title'
              }
            >
              {resourceItem.ResourceTitle}{' '}
              <span className="sr-only">
                {' '}
                Press Enter Key to visit the external link
              </span>
            </a>

            {null !== resourceItem.EffectiveFrom ? (
              <div className="effective-date-resources">
                <span className="date-description">
                  `Effective Date - ${formatDate(resourceItem.EffectiveFrom)}`
                </span>
              </div>
            ) : (
              ''
            )}
          </div>
        }
      </>
    );
  }
};

export const ResourcesCard = ({
  truncateResourceTitle = true,
  data: resources = [],
  showDescription,
}) => {
  const { formatDate } = useDate();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const onResourceItemClick = (resourceItem) => {
    if ('External Link' === resourceItem.ContentType) {
      window.open(resourceItem.ExternalUrl);
    } else if ('Document' === resourceItem.ContentType) {
      downloadFile(resourceItem.DocumentOid, resourceItem.Slug);
    } else if (
      'Page' === resourceItem.ContentType &&
      'External' === resourceItem.ContentReference
    ) {
      localStorage.removeItem('resourece-page-oid');
      localStorage.setItem('resourece-page-oid', resourceItem.PageOID);
      window.open(`/member/resource/${resourceItem.Slug}`);
    } else {
      localStorage.removeItem('resourece-page-oid');
      localStorage.setItem('resourece-page-oid', resourceItem.PageOID);
      navigate(`/member/resource/${resourceItem.Slug}`);
    }
  };
  return (
    <div className="list-resources">
      {resources.length > 0 ? (
        <ListGroup role="list">
          {resources.map((resourceItem, index) => (
            <>
              <OverlayTrigger
                placement="top"
                trigger="hover"
                key={resourceItem.ResourceOid}
                overlay={
                  <Popover>
                    <Popover.Content>
                      <div>
                        <span className="popover-resource-title">
                          {resourceItem.ResourceTitle}
                        </span>
                        <br />
                        {'' !== resourceItem.ResourceDescription ? (
                          <span>{resourceItem.ResourceDescription}</span>
                        ) : (
                          ''
                        )}
                      </div>
                    </Popover.Content>
                  </Popover>
                }
              >
                <ListGroupItem
                  key={index}
                  className="list-resource-item"
                  role="listitem"
                  onClick={() => onResourceItemClick(resourceItem)}
                >
                  {getResourceCards(
                    resourceItem,
                    truncateResourceTitle,
                    formatDate,
                  )}
                  {'Document' === resourceItem.ContentType && (
                    <Fragment>
                      <div className="resource-file-type">
                        <div className={'resource-item-info-icon'}>
                          <ResourcesSVG.DownloadSVG className="fill-color" />
                        </div>
                      </div>

                      {/*FUTURE: we can convert this block to resuable component */}
                      <div className="resource-content">
                        <a
                          role="button"
                          aria-label={`${resourceItem.ResourceTitle} ${
                            null !== resourceItem.EffectiveFrom
                              ? 'Effective Date - '.concat(
                                  formatDate(resourceItem.EffectiveFrom),
                                )
                              : 'Effective Date - N/A'
                          }, Press Enter Key to download the document`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter')
                              onResourceItemClick(resourceItem);
                          }}
                          className={
                            truncateResourceTitle
                              ? 'resource-link-title'
                              : 'display-full-resource-link-title'
                          }
                        >
                          {resourceItem.ResourceTitle}{' '}
                          <span className="sr-only">
                            {' '}
                            Press Enter Key to download the document
                          </span>
                        </a>

                        {null !== resourceItem.EffectiveFrom ? (
                          <div className="effective-date-resources">
                            <span className="date-description">
                              `Effective Date - $
                              {formatDate(resourceItem.EffectiveFrom)}`
                            </span>
                          </div>
                        ) : (
                          ''
                        )}

                        {showDescription && resourceItem.ResourceDescription ? (
                          <span className="text-description">
                            {' '}
                            {resourceItem.ResourceDescription}{' '}
                          </span>
                        ) : (
                          ''
                        )}
                      </div>
                    </Fragment>
                  )}

                  {'Page' === resourceItem.ContentType && (
                    <Fragment>
                      <div className="resource-file-type">
                        <div className={'resource-item-info-icon'}>
                          <ResourcesSVG.DocumentSVG className="fill-color" />
                        </div>
                      </div>

                      {/*FUTURE: we can convert this block to resuable component */}
                      {'External' === resourceItem.ContentReference && (
                        <div className="resource-content">
                          <a
                            role="button"
                            aria-label={`${resourceItem.ResourceTitle} ${
                              null !== resourceItem.EffectiveFrom
                                ? 'Effective Date - '.concat(
                                    formatDate(resourceItem.EffectiveFrom),
                                  )
                                : 'Effective Date - N/A'
                            }, Press Enter Key to visit the link`}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter')
                                onResourceItemClick(resourceItem);
                            }}
                            className={
                              truncateResourceTitle
                                ? 'resource-link-title'
                                : 'display-full-resource-link-title'
                            }
                          >
                            {resourceItem.ResourceTitle}{' '}
                            <span className="sr-only">
                              {' '}
                              Press Enter Key to visit the link
                            </span>
                          </a>

                          {null !== resourceItem.EffectiveFrom ? (
                            <div className="effective-date-resources">
                              <span className="date-description">
                                `Effective Date - $
                                {formatDate(resourceItem.EffectiveFrom)}`
                              </span>
                            </div>
                          ) : (
                            ''
                          )}

                          {showDescription &&
                          resourceItem.ResourceDescription ? (
                            <span className="text-description">
                              {' '}
                              {resourceItem.ResourceDescription}{' '}
                            </span>
                          ) : (
                            ''
                          )}
                        </div>
                      )}

                      {/*FUTURE: we can convert this block to resuable component */}
                      {'Internal' === resourceItem.ContentReference && (
                        <div className="resource-content">
                          <a
                            role="button"
                            aria-label={`${resourceItem.ResourceTitle} ${
                              null !== resourceItem.EffectiveFrom
                                ? 'Effective Date - '.concat(
                                    formatDate(resourceItem.EffectiveFrom),
                                  )
                                : 'Effective Date - N/A'
                            }, Press Enter Key to visit the link`}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter')
                                onResourceItemClick(resourceItem);
                            }}
                            className={
                              truncateResourceTitle
                                ? 'resource-link-title'
                                : 'display-full-resource-link-title'
                            }
                          >
                            {resourceItem.ResourceTitle}{' '}
                            <span className="sr-only">
                              {' '}
                              Press Enter Key to visit the link
                            </span>
                          </a>
                          {null !== resourceItem.EffectiveFrom ? (
                            <div className="effective-date-resources">
                              <span className="date-description">
                                `Effective Date - $
                                {formatDate(resourceItem.EffectiveFrom)}`
                              </span>
                            </div>
                          ) : (
                            ''
                          )}
                          {showDescription &&
                          resourceItem.ResourceDescription ? (
                            <span className="text-description">
                              {' '}
                              {resourceItem.ResourceDescription}{' '}
                            </span>
                          ) : (
                            ''
                          )}
                        </div>
                      )}
                    </Fragment>
                  )}

                  {showDescription ? (
                    ''
                  ) : (
                    <div className="pull-right">
                      <div className="resource-arrow-icon">
                        <ResourcesSVG.ArrowSVG className="fill-color" />
                      </div>
                    </div>
                  )}
                </ListGroupItem>
              </OverlayTrigger>
            </>
          ))}
        </ListGroup>
      ) : (
        <span role="alert">{t('member.resource.no_records')}</span>
      )}
    </div>
  );
};
